import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { getKnowledgeBaseHealth, getKnowledgeBaseMetrics } from '../services/knowledgeService';
import type { KnowledgeBaseHealth, KnowledgeRetrievalMetrics } from '../types/Knowledge';
import styles from './KnowledgeBaseOperationalSummaryPanel.module.css';

const formatNumber = (value: number): string => new Intl.NumberFormat().format(value);

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const KnowledgeBaseOperationalSummaryPanel: React.FC = () => {
  const { t } = useTranslation('knowledge');
  const { isPlatformOperator } = useAuth();
  const canViewOperations = isPlatformOperator;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [health, setHealth] = React.useState<KnowledgeBaseHealth | null>(null);
  const [metrics, setMetrics] = React.useState<KnowledgeRetrievalMetrics | null>(null);

  const load = React.useCallback(async () => {
    if (!canViewOperations) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [nextHealth, nextMetrics] = await Promise.all([
        getKnowledgeBaseHealth(),
        getKnowledgeBaseMetrics(24),
      ]);

      setHealth(nextHealth);
      setMetrics(nextMetrics);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('ops.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [canViewOperations, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (!canViewOperations) {
    return null;
  }

  return (
    <section className="company-section" style={{ marginTop: 16 }}>
      <div className={styles.panelHeader}>
        <div>
          <h3>{t('ops.title')}</h3>
          <p className={styles.panelIntro}>{t('ops.intro')}</p>
        </div>
        <div className={styles.panelActions}>
          <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
            {t('ops.refresh')}
          </button>
        </div>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {loading || !health || !metrics ? (
        <div className={styles.loadingWrap}>
          <Spinner text={t('ops.loading')} />
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.ingestionStatus')}</div>
              <div className={styles.statusRow}>
                <span className={`${styles.statusBadge} ${health.isIngestionStuck ? styles.statusStuck : styles.statusHealthy}`}>
                  {health.isIngestionStuck ? t('ops.status.stuck') : t('ops.status.healthy')}
                </span>
                <span className={styles.metricSubtext}>
                  {t('ops.thresholdSeconds', { seconds: health.stuckQueueAlertThresholdSeconds })}
                </span>
              </div>
            </article>

            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.queueLoad')}</div>
              <div className={styles.metricValue}>
                {formatNumber(health.jobsQueued + health.jobsProcessing)}
              </div>
              <div className={styles.metricSubtext}>
                {t('ops.queueBreakdown', { queued: health.jobsQueued, processing: health.jobsProcessing })}
              </div>
            </article>

            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.deadLetter')}</div>
              <div className={styles.metricValue}>{formatNumber(health.jobsDeadLetter)}</div>
              <div className={styles.metricSubtext}>
                {t('ops.failedJobs', { count: health.jobsFailed })}
              </div>
            </article>

            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.emptyResultRate')}</div>
              <div className={styles.metricValue}>{formatPercent(metrics.emptyResultRate)}</div>
              <div className={styles.metricSubtext}>
                {t('ops.sampleWindow', { count: metrics.sampledRequests })}
              </div>
            </article>

            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.latency')}</div>
              <div className={styles.metricValue}>{`${formatNumber(metrics.p50LatencyMilliseconds)} / ${formatNumber(metrics.p95LatencyMilliseconds)} ms`}</div>
              <div className={styles.metricSubtext}>{t('ops.latencyHint')}</div>
            </article>

            <article className={styles.metricCard}>
              <div className={styles.metricLabel}>{t('ops.cards.documents')}</div>
              <div className={styles.metricValue}>{formatNumber(health.documentsReady)}</div>
              <div className={styles.metricSubtext}>
                {t('ops.docsBreakdown', { total: health.documentsTotal, failed: health.documentsFailed })}
              </div>
            </article>
          </div>

          <div className={styles.metricSubtext} style={{ marginTop: 10 }}>
            {t('ops.generatedAt', { at: formatDateTime(health.generatedAt) })}
          </div>
        </>
      )}
    </section>
  );
};

export default KnowledgeBaseOperationalSummaryPanel;
